// ─── Security Utilities ───────────────────────────────────────────────────────
// Sanitize and validate all user inputs before sending via EmailJS

/**
 * Strips HTML tags and dangerous characters from a string
 * Prevents XSS attacks in form submissions
 */
export const sanitizeInput = (str = "") => {
  return str
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;")
    .trim();
};

/**
 * Sanitizes an entire form data object
 */
export const sanitizeFormData = (data = {}) => {
  const sanitized = {};
  Object.entries(data).forEach(([key, value]) => {
    sanitized[key] = typeof value === "string" ? sanitizeInput(value) : value;
  });
  return sanitized;
};

/**
 * Validates email format
 */
export const isValidEmail = (email = "") => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

/**
 * Validates phone number — allows digits, spaces, +, -
 */
export const isValidPhone = (phone = "") => {
  return /^[\d\s\-+()]{7,20}$/.test(phone.trim());
};

/**
 * Checks string length is within safe limits
 */
export const isWithinLength = (str = "", max = 1000) => {
  return str.trim().length <= max;
};

/**
 * Rate limiting helper — prevents form spam
 * Returns true if submission is allowed, false if too soon
 */
const lastSubmissionTimes = {};

export const isRateLimited = (formId = "default", cooldownMs = 30000) => {
  const now = Date.now();
  const last = lastSubmissionTimes[formId] || 0;

  if (now - last < cooldownMs) {
    return true; // rate limited — too soon
  }

  lastSubmissionTimes[formId] = now;
  return false; // allowed
};

/**
 * Full contact form validation with sanitization
 */
export const validateAndSanitizeContactForm = (formData) => {
  const errors = {};

  // Required fields
  if (!formData.name?.trim())    errors.name    = "Name is required";
  if (!formData.email?.trim())   errors.email   = "Email is required";
  if (!formData.phone?.trim())   errors.phone   = "Phone is required";
  if (!formData.service?.trim()) errors.service = "Please select a service";

  // Format validation
  if (formData.email && !isValidEmail(formData.email))
    errors.email = "Please enter a valid email address";

  if (formData.phone && !isValidPhone(formData.phone))
    errors.phone = "Please enter a valid phone number";

  // Length limits — prevent payload attacks
  if (!isWithinLength(formData.name,        100))  errors.name    = "Name is too long";
  if (!isWithinLength(formData.email,       254))  errors.email   = "Email is too long";
  if (!isWithinLength(formData.message,     2000)) errors.message = "Message is too long (max 2000 characters)";
  if (!isWithinLength(formData.companyName, 200))  errors.companyName = "Company name is too long";

  const hasErrors = Object.keys(errors).length > 0;

  return {
    errors,
    isValid: !hasErrors,
    sanitized: hasErrors ? null : sanitizeFormData(formData),
  };
};

/**
 * Full careers form validation with sanitization
 */
export const validateAndSanitizeCareersForm = (formData) => {
  const errors = {};

  if (!formData.name?.trim())  errors.name  = "Name is required";
  if (!formData.email?.trim()) errors.email = "Email is required";
  if (!formData.phone?.trim()) errors.phone = "Phone is required";

  if (formData.email && !isValidEmail(formData.email))
    errors.email = "Please enter a valid email address";

  if (formData.phone && !isValidPhone(formData.phone))
    errors.phone = "Please enter a valid phone number";

  if (!isWithinLength(formData.name,    100))  errors.name    = "Name is too long";
  if (!isWithinLength(formData.email,   254))  errors.email   = "Email is too long";
  if (!isWithinLength(formData.message, 3000)) errors.message = "Cover letter is too long (max 3000 characters)";

  const hasErrors = Object.keys(errors).length > 0;

  return {
    errors,
    isValid: !hasErrors,
    sanitized: hasErrors ? null : sanitizeFormData(formData),
  };
};